import { useLayoutEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { gsap } from "@/lib/gsap";
import { LOGO_URL } from "@/lib/constants";

const HEADLINE = ["NOT YOUR", "AVERAGE", "PHOTOGRAPHER"];

export default function HeroSequence({ photos, projects }) {
  const root = useRef(null);

  useLayoutEffect(() => {
    if (photos.length < 3 || !projects.length) return undefined;
    const ctx = gsap.context(() => {
      const q = gsap.utils.selector(root);
      const tl = gsap.timeline({
        defaults: { ease: "none" },
        scrollTrigger: { trigger: root.current, start: "top top", end: "+=900%", pin: true, scrub: 0.5, anticipatePin: 1, pinType: "fixed", refreshPriority: 3 },
      });

      // Scene 2 — headline leaves, wordmark enters over the single photo
      tl.to(q(".hl-line"), { yPercent: -140, opacity: 0, stagger: 0.1, duration: 1, ease: "power2.in" }, 0)
        .to(q(".scroll-hint, .hero-eyebrow"), { opacity: 0, duration: 0.4 }, 0)
        .to(q(".p1"), { xPercent: -70, scale: 1.2, duration: 1.4 }, 0.1)
        .fromTo(q(".wordmark"), { scale: 0.3, opacity: 0 }, { scale: 1.45, opacity: 1, duration: 1.3, ease: "power1.out" }, 0.9)
        .to(q(".wordmark-mask"), { opacity: 0.9, duration: 0.7 }, 1.5)
        .to(q(".p1"), { opacity: 0.35, scale: 1.05, duration: 0.8 }, 1.3)

        // Scene 3 — wordmark flies through camera, photos build depth
        .to(q(".wordmark"), { scale: 3.4, opacity: 0, duration: 1.2, ease: "power1.in" }, 2.4)
        .to(q(".p1"), { opacity: 1, scale: 1.4, xPercent: -60, duration: 1.2 }, 2.6)
        .fromTo(q(".p2"), { xPercent: -150, scale: 0.32, opacity: 0 }, { xPercent: -55, opacity: 1, duration: 1.3 }, 2.8)
        .fromTo(q(".p3"), { yPercent: 180, scale: 0.75, opacity: 0 }, { yPercent: 10, opacity: 0.85, duration: 1.3 }, 3.1)

        // navigate through space
        .to(q(".p1"), { xPercent: -150, scale: 1.05, duration: 1.3 }, 4.2)
        .to(q(".p2"), { xPercent: 0, scale: 0.6, duration: 1.3 }, 4.2)
        .to(q(".p3"), { yPercent: -150, scale: 0.6, duration: 1.3 }, 4.2)

        // Scene 4 — takeover
        .to(q(".p2"), { scale: 1, duration: 1.3, ease: "power2.inOut" }, 5.4)
        .to([q(".p1"), q(".p3")], { opacity: 0, duration: 0.6 }, 5.5)
        .fromTo(q(".proj-meta-0 > *"), { yPercent: 120, opacity: 0 }, { yPercent: 0, opacity: 1, stagger: 0.08, duration: 0.6, ease: "power2.out" }, 6.2);

      // Scene 5 — project transitions inside the same frame
      projects.forEach((_, i) => {
        if (i === 0) return;
        const t = 7 + (i - 1) * 1.2;
        tl.fromTo(q(`.proj-img-${i}`), { clipPath: "inset(100% 0% 0% 0%)" }, { clipPath: "inset(0% 0% 0% 0%)", duration: 0.9, ease: "power2.inOut" }, t)
          .fromTo(q(`.proj-img-${i} img`), { scale: 1.3 }, { scale: 1, duration: 0.9 }, t)
          .to(q(`.proj-meta-${i - 1} > *`), { yPercent: -120, opacity: 0, stagger: 0.05, duration: 0.4, ease: "power2.in" }, t)
          .fromTo(q(`.proj-meta-${i} > *`), { yPercent: 120, opacity: 0 }, { yPercent: 0, opacity: 1, stagger: 0.06, duration: 0.5, ease: "power2.out" }, t + 0.35);
      });
      tl.to({}, { duration: 0.8 });
    }, root);
    return () => ctx.revert();
  }, [photos, projects]);

  return (
    <section ref={root} id="home" className="relative h-[100svh] w-full overflow-hidden bg-[#050708]" data-testid="hero-sequence">
      <p className="hero-eyebrow absolute top-24 left-5 sm:left-8 z-20 font-mono2 text-[10px] sm:text-xs tracking-[0.35em] text-[#02AACF]" data-testid="hero-location">
        NASSAU, BAHAMAS
      </p>

      {/* Scene 1 — single photograph, partially cropped right */}
      <div className="p1 absolute top-[16vh] sm:top-[10vh] right-[-8vw] h-[60vh] sm:h-[78vh] w-[56vw] sm:w-[34vw] z-[5] will-change-transform" data-cursor="view">
        <img src={photos[0]} alt="LUHAI photography" className="h-full w-full object-cover object-[50%_28%]" loading="eager" />
      </div>

      {/* headline */}
      <h1 className="absolute left-5 sm:left-8 bottom-[18vh] sm:bottom-[14vh] z-10 font-display font-extrabold text-white leading-[0.88] tracking-[-0.03em] text-[clamp(1.5rem,6.5vw,7.8rem)]" data-testid="hero-headline">
        {HEADLINE.map((l) => (
          <span key={l} className="block overflow-hidden"><span className="hl-line block will-change-transform">{l}</span></span>
        ))}
      </h1>

      <p className="scroll-hint absolute bottom-7 left-5 sm:left-8 z-10 font-mono2 text-[10px] sm:text-xs tracking-[0.3em] text-white/50" data-testid="hero-scroll-hint">
        SCROLL TO EXPLORE ↓
      </p>

      {/* Scene 2 — wordmark (official logo image) with photo masked through the letters */}
      <div className="wordmark absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[92vw] z-[8] opacity-0 will-change-transform" data-testid="hero-wordmark">
        <img src={LOGO_URL} alt="LUHAI" className="w-full h-auto block" draggable={false} />
        <div
          className="wordmark-mask absolute inset-0 opacity-0"
          style={{
            backgroundImage: `url(${photos[0]})`, backgroundSize: "cover", backgroundPosition: "center",
            WebkitMaskImage: `url(${LOGO_URL})`, maskImage: `url(${LOGO_URL})`,
            WebkitMaskSize: "contain", maskSize: "contain", WebkitMaskRepeat: "no-repeat", maskRepeat: "no-repeat",
            WebkitMaskPosition: "center", maskPosition: "center",
          }}
        />
      </div>

      {/* Scene 3 — distant photo */}
      <div className="p3 absolute top-[22vh] left-[8vw] sm:left-[14vw] h-[26vh] sm:h-[34vh] w-[36vw] sm:w-[18vw] z-[3] opacity-0 will-change-transform">
        <img src={photos[2]} alt="LUHAI photography" className="h-full w-full object-cover object-[50%_28%] brightness-75" loading="eager" />
      </div>

      {/* Scene 3/4/5 — the photograph that takes over and transforms between projects */}
      <div className="p2 absolute top-[6vh] left-[5vw] h-[88vh] w-[90vw] z-[6] opacity-0 will-change-transform overflow-hidden" data-cursor="view" data-testid="hero-takeover">
        {projects.map((p, i) => (
          <div key={p.title} className={`proj-img-${i} absolute inset-0`} style={i > 0 ? { clipPath: "inset(100% 0% 0% 0%)" } : undefined}>
            <img src={p.url} alt={p.title} className="h-full w-full object-cover object-[50%_28%] will-change-transform" loading="eager" />
          </div>
        ))}
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent pointer-events-none" />
      </div>

      {/* project meta layers */}
      <div className="absolute left-[9vw] bottom-[12vh] z-[9] pointer-events-none" data-testid="hero-project-meta">
        {projects.map((p, i) => (
          <div key={p.title} className={`proj-meta-${i} absolute bottom-0 left-0 flex flex-col gap-1 ${i === 0 ? "" : ""}`}>
            <span className="font-mono2 text-[#02AACF] text-sm sm:text-base tracking-[0.3em] opacity-0">{String(i + 1).padStart(2, "0")}</span>
            <span className="font-display font-extrabold text-white text-[clamp(2.6rem,8vw,7.5rem)] leading-[0.9] tracking-[-0.03em] whitespace-nowrap opacity-0">{p.title}</span>
            <span className="font-mono2 text-white/70 text-[10px] sm:text-xs tracking-[0.3em] mt-3 opacity-0">NASSAU, BAHAMAS</span>
            <Link to="/work" className="pointer-events-auto font-mono2 text-white text-xs sm:text-sm tracking-[0.25em] mt-5 inline-flex w-fit border-b border-white/40 pb-1 hover:text-[#02AACF] hover:border-[#02AACF] transition-colors opacity-0" data-testid={`hero-view-project-${i}`}>
              VIEW PROJECT ↗
            </Link>
          </div>
        ))}
      </div>
    </section>
  );
}
